import React, { useState } from 'react';
import { ChevronDown, FileText, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';

import { cn } from '../../../lib/utils';
import { openSignedEnvelopePdf } from '../../../services/documents';

// Compact "Signed ✓" chip with the signed-paperwork list behind a click —
// shared by the Partners table and the Referral Payouts table so paperwork
// reads identically everywhere: status at a glance, documents on demand.
//
// Renders nothing when there are no docs — the caller owns the empty states
// (they differ: "Legacy" on Partners, "Pending" on Payouts).
const PaperworkChip = ({ docs = [], canView = false, label = 'Signed ✓' }) => {
  const [open, setOpen] = useState(false);
  const [viewingId, setViewingId] = useState(null);

  if (!docs.length) return null;

  const view = async (envelopeId) => {
    setViewingId(envelopeId);
    try {
      await openSignedEnvelopePdf(envelopeId);
    } catch (e) {
      toast.error(e?.response?.data?.msg || 'Could not open the document.');
    } finally {
      setViewingId(null);
    }
  };

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => canView && setOpen((o) => !o)}
        title={
          canView
            ? 'View signed paperwork'
            : `${docs.length} signed document${docs.length === 1 ? '' : 's'} on file`
        }
        className={cn(
          'inline-flex items-center whitespace-nowrap rounded-full bg-status-success-bg px-2 py-0.5 text-xs font-medium text-status-success-fg',
          canView &&
            'cursor-pointer transition-shadow hover:ring-1 hover:ring-status-success',
        )}
      >
        {label}
        {canView ? <ChevronDown className="ml-1 h-3 w-3" /> : null}
      </button>
      {open ? (
        <>
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="absolute left-0 top-7 z-50 w-64 rounded-xl border border-border bg-card p-1 text-left shadow-lg">
            <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Signed paperwork
            </div>
            {docs.map((doc) => (
              <button
                key={doc.envelopeId}
                type="button"
                onClick={() => view(doc.envelopeId)}
                disabled={Boolean(viewingId)}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground transition-colors hover:bg-accent disabled:opacity-60"
              >
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate text-left">{doc.name}</span>
                {doc.source === 'uploaded' ? (
                  <span className="shrink-0 text-xs text-muted-foreground">uploaded</span>
                ) : null}
                {viewingId === doc.envelopeId ? (
                  <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                ) : doc.completedAt ? (
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {new Date(doc.completedAt).toLocaleDateString()}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default PaperworkChip;
